/**
 * analytics.ts — Fire-and-forget localStorage event logger.
 *
 * R16 BA-1: localStorage-only telemetry. No external service (R17 wires that).
 *
 * Storage key : gdg.analytics.sessions
 * Format      : array of AnalyticsSession (JSON; each session has JSONL-style events array)
 * Cap         : 50 sessions — oldest evicted FIFO when 51st session is pushed.
 * SSR-safe    : all localStorage access guarded by `typeof window !== "undefined"`.
 * Never throws: recordEvent wraps all writes in try/catch.
 *
 * ## Usage for Builders BX / BY / BZ (minigame instrumentation)
 *
 *   import { recordEvent } from "../analytics/analytics.js";
 *
 *   recordEvent({
 *     type: "minigame-result",
 *     t: Date.now(),
 *     minigame: "quiz",        // "quiz" | "scout" | "auction" | "climate-vote" | "energy-dilemma" | "trade" | "pollution-tax"
 *     seat: currentSeat,       // 0 | 1 | 2 | 3
 *     outcome: "correct",      // free-form string — describe the player's result
 *     tokensDelta: 15,         // net token change from this minigame (positive = gain)
 *   });
 *
 * That's it. Fire-and-forget — no await, no error handling needed on the call site.
 */

import type { SeatIndex } from "../store/game-store.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Archetype perk — mirrors playerPerks values in game-store. */
export type Archetype = "engineer" | "scientist" | "activist" | "politician";

export type AnalyticsEvent =
  | {
      type: "game-start";
      t: number;
      seatPerks: [Archetype, Archetype, Archetype, Archetype];
      rngSeed: number;
    }
  | {
      type: "round-end";
      t: number;
      round: number;
      era: number;
      tokens: [number, number, number, number];
      energy: [number, number, number, number];
      pollution: [number, number, number, number];
      activeSeats: number;
    }
  | {
      type: "card-played";
      t: number;
      seat: SeatIndex;
      cardId: string;
      outcome: "played" | "blocked" | "mitigated";
    }
  | {
      type: "minigame-result";
      t: number;
      minigame:
        | "quiz"
        | "scout"
        | "auction"
        | "climate-vote"
        | "energy-dilemma"
        | "trade"
        | "pollution-tax";
      seat: SeatIndex;
      outcome: string;
      tokensDelta: number;
    }
  | {
      type: "game-end";
      t: number;
      endReason: string;
      winnerSeat: SeatIndex | null;
      round: number;
    };

export interface AnalyticsSession {
  id: string;
  startedAt: number;
  events: AnalyticsEvent[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = "gdg.analytics.sessions";
const MAX_SESSIONS = 50;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** True when localStorage is accessible (guards SSR + Safari private mode). */
function _storageAvailable(): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem("__gdg_test__", "1");
    window.localStorage.removeItem("__gdg_test__");
    return true;
  } catch {
    return false;
  }
}

/** Read raw session array from localStorage. Returns [] on any parse failure. */
function _readSessions(): AnalyticsSession[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as AnalyticsSession[];
  } catch {
    return [];
  }
}

/** Persist session array. Silently drops on quota / private-mode error. */
function _writeSessions(sessions: AnalyticsSession[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch {
    // Quota exceeded or Safari private mode — swallow silently.
  }
}

// ---------------------------------------------------------------------------
// Session ID — current game session pointer
// ---------------------------------------------------------------------------

/** Module-private: the session ID that was created on the last game-start. */
let _currentSessionId: string | null = null;

/** Generate a unique session ID (timestamp + small random suffix). */
function _newSessionId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * recordEvent — append an event to the current analytics session.
 *
 * - If type === "game-start": creates a new session (evicts oldest if cap exceeded).
 * - All other types: append to current session (no-op when no session exists yet).
 * - Always fire-and-forget: wraps entire body in try/catch, never throws.
 */
export function recordEvent(event: AnalyticsEvent): void {
  try {
    if (!_storageAvailable()) return;

    if (event.type === "game-start") {
      // Start a fresh session.
      _currentSessionId = _newSessionId();
      const newSession: AnalyticsSession = {
        id: _currentSessionId,
        startedAt: event.t,
        events: [event],
      };

      const sessions = _readSessions();
      sessions.push(newSession);

      // Evict oldest sessions when over cap.
      while (sessions.length > MAX_SESSIONS) {
        sessions.shift();
      }

      _writeSessions(sessions);
      return;
    }

    // All non-start events: append to current session.
    if (_currentSessionId === null) return;

    const sessions = _readSessions();
    const sessionIdx = sessions.findIndex((s) => s.id === _currentSessionId);
    if (sessionIdx === -1) return;

    // Safe to mutate — we just deserialized from JSON.
    const session = sessions[sessionIdx];
    if (session === undefined) return;
    session.events.push(event);
    _writeSessions(sessions);
  } catch {
    // Never throw from recordEvent.
  }
}

/**
 * exportSessions — returns all stored sessions as a JSONL string.
 * Each line is one session serialized to JSON.
 * Returns empty string when localStorage is unavailable.
 */
export function exportSessions(): string {
  if (!_storageAvailable()) return "";
  const sessions = _readSessions();
  return sessions.map((s) => JSON.stringify(s)).join("\n");
}

/**
 * clearSessions — delete all stored analytics sessions.
 * Resets the in-memory current-session pointer too.
 */
export function clearSessions(): void {
  if (!_storageAvailable()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    _currentSessionId = null;
  } catch {
    // Swallow private-mode errors.
  }
}

/**
 * getSessionCount — returns the number of stored sessions (0..50).
 */
export function getSessionCount(): number {
  if (!_storageAvailable()) return 0;
  return _readSessions().length;
}
